// NEXUS — Facebook-style 9-step signup wizard (replaces the old 4-step signup).
// LOGIN pane stays on the left column, unchanged.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hexagon, ArrowRight, ArrowLeft, Check, Shield, User, Upload, Camera, Video, Sparkles, Copy, Share2, Users, Mail, ExternalLink } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import PayPalCheckout from "../components/PayPalCheckout";
import { toast, Toaster } from "sonner";

const COUNTRIES = ["United States","United Kingdom","Canada","Australia","New Zealand","Germany","France","Spain","Italy","Netherlands","Sweden","Japan","South Korea","Singapore","Brazil","Mexico","India","South Africa","Other"];
const SOCIAL_KEYS = [
  { id:"tiktok",    label:"TikTok",    prefix:"@",     url:(h)=>`https://www.tiktok.com/@${h.replace('@','')}` },
  { id:"instagram", label:"Instagram", prefix:"@",     url:(h)=>`https://instagram.com/${h.replace('@','')}` },
  { id:"facebook",  label:"Facebook",  prefix:"",      url:(h)=>h.startsWith("http")?h:`https://facebook.com/${h}` },
  { id:"snapchat",  label:"Snapchat",  prefix:"@",     url:(h)=>`https://snapchat.com/add/${h.replace('@','')}` },
  { id:"youtube",   label:"YouTube",   prefix:"@",     url:(h)=>`https://youtube.com/@${h.replace('@','')}` },
  { id:"twitter",   label:"X / Twitter",prefix:"@",    url:(h)=>`https://x.com/${h.replace('@','')}` },
];

// eslint-disable-next-line max-lines-per-function
export default function Auth() {
  const { login, refresh } = useAuth();
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email:"", password:"" });
  const [f, setF] = useState({
    // 1. Basics
    full_name:"", dob:"", address:"", country:"", email:"", password:"",
    // 2. Studio + ref
    studio_signup:false, ref: params.get("ref") || "",
    // 3. Studio KYC (only if studio_signup)
    tax_country:"", photo_id_url:"", paypal_email:"",
    // 4. Interests
    interests: [],
    // 5. Profile
    display_name:"", bio:"", avatar_url:"", banner_url:"", intro_video_url:"",
    socials: {}, website:"",
    show_dob:false, show_marital:false, marital_status:"", show_kids:false, is_parent:false,
    // legal
    agreed_to_tos:false, agreed_to_kyc:false, agreed_to_billing:false, agreed_to_community:false,
  });
  const patch = (p) => setF((x) => ({ ...x, ...p }));

  // Auto-login owner via ?owner=1
  useEffect(() => {
    (async () => {
      if (params.get("owner") === "1") {
        try { const u = await login("owner@nexus.io", "nexus2026"); nav(`/u/${u.id}`); } catch {}
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const age = useMemo(() => {
    if (!f.dob) return "";
    const d = new Date(f.dob); if (isNaN(d)) return "";
    return Math.floor((Date.now() - d.getTime()) / (365.25 * 86400e3));
  }, [f.dob]);

  const submitLogin = async () => {
    setLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      toast.success(`Welcome back, ${u.display_name}`);
      nav(`/u/${u.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Login failed"); }
    setLoading(false);
  };

  const finalize = async (extra = {}) => {
    setLoading(true);
    try {
      const payload = {
        email: f.email, password: f.password, display_name: f.display_name || f.full_name.split(" ")[0],
        role: "viewer",
        intended_role: f.studio_signup ? "creator" : "viewer",
        studio_signup: f.studio_signup,
        ref: f.ref || undefined,
        // extended fields
        full_name: f.full_name, dob: f.dob, address: f.address, country: f.country,
        tax_country: f.tax_country || f.country, paypal_email: f.paypal_email,
        photo_id_url: f.photo_id_url,
        interests: f.interests, bio: f.bio,
        avatar_url: f.avatar_url, banner_url: f.banner_url, intro_video_url: f.intro_video_url,
        socials: f.socials, website: f.website,
        show_dob: f.show_dob, show_marital: f.show_marital, marital_status: f.marital_status, show_kids: f.show_kids,
        is_parent: f.is_parent,
        agreed_to_tos: f.agreed_to_tos, agreed_to_kyc: f.agreed_to_kyc, agreed_to_billing: f.agreed_to_billing,
        agreed_to_community: f.agreed_to_community,
        ...extra,
      };
      const { data } = await api.post("/auth/signup", payload);
      localStorage.setItem("nexus_token", data.token);
      await refresh();
      toast.success(`Welcome to NEXUS, ${data.user.display_name}`);
      nav(`/u/${data.user.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Signup failed"); }
    setLoading(false);
  };

  const next = () => {
    // Validation per step
    if (step === 1) {
      if (!f.full_name || !f.dob || !f.address || !f.country || !f.email || !f.password) return toast.error("Fill every field");
      if (f.password.length < 8) return toast.error("Password must be 8+ characters");
      if (Number(age) < 13) return toast.error("Under-13 accounts aren't allowed. Ask a parent to open one for you.");
    }
    if (step === 3 && f.studio_signup) {
      if (!f.tax_country || !f.paypal_email) return toast.error("Tax country and PayPal email required for Studio Tier");
    }
    if (step === 5 && !f.display_name) return toast.error("Pick a display name");
    if (step === 9) {
      if (!f.agreed_to_tos || !f.agreed_to_community) return toast.error("Accept the Terms + Community Guidelines to continue");
      finalize();
      return;
    }
    // If not studio and we're on step 2 → skip step 3 (studio KYC)
    if (step === 2 && !f.studio_signup) { setStep(4); return; }
    setStep((s) => Math.min(9, s + 1));
  };
  const back = () => {
    if (step === 4 && !f.studio_signup) { setStep(2); return; }
    setStep((s) => Math.max(1, s - 1));
  };

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-start min-h-[80vh]" data-testid="auth-page">
      <Toaster theme="dark" position="top-center" />

      {/* LEFT — pitch (login only) or wizard progress */}
      <div className="space-y-6">
        <Hexagon className="w-12 h-12 text-nexus-cyan animate-sigil-spin" />
        <div>
          <div className="label-eyebrow mb-3">// {mode === "login" ? "return" : `step ${step} of 9`}</div>
          <h1 className="font-display font-black text-5xl lg:text-7xl tracking-tighter">Enter NEXUS.</h1>
        </div>
        {mode === "login" ? (
          <p className="text-white/50 max-w-md">Every stream, every tip, every stat lives on your profile — badges carry your XP, tips, channel and wallet across every screen.</p>
        ) : (
          <ProgressList step={step} studio={f.studio_signup} />
        )}
      </div>

      {/* RIGHT — form */}
      <div className="glass p-8 space-y-5 max-w-2xl">
        <div className="flex gap-4">
          <button onClick={() => { setMode("login"); }} className={`text-xs tracking-[0.3em] uppercase font-display ${mode === "login" ? "text-nexus-cyan" : "text-white/40"}`} data-testid="mode-login">Login</button>
          <button onClick={() => { setMode("signup"); setStep(1); }} className={`text-xs tracking-[0.3em] uppercase font-display ${mode === "signup" ? "text-nexus-cyan" : "text-white/40"}`} data-testid="mode-signup">Sign up</button>
        </div>

        {mode === "login" ? (
          <>
            <input placeholder="Email" type="email" autoCapitalize="none" autoCorrect="off" autoComplete="email" value={loginForm.email} onChange={(e)=>setLoginForm({...loginForm, email:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-3" data-testid="auth-email" />
            <input placeholder="Password" type="password" value={loginForm.password} onChange={(e)=>setLoginForm({...loginForm, password:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-3" data-testid="auth-password" />
            <button onClick={submitLogin} disabled={loading} className="btn-nexus w-full justify-center" data-testid="auth-submit">{loading ? "…" : "Enter"}</button>
          </>
        ) : (
          <>
            {step === 1 && <Step1 f={f} patch={patch} age={age} />}
            {step === 2 && <Step2 f={f} patch={patch} />}
            {step === 3 && <Step3 f={f} patch={patch} finalize={finalize} />}
            {step === 4 && <Step4 f={f} patch={patch} />}
            {step === 5 && <Step5 f={f} patch={patch} />}
            {step === 6 && <Step6 f={f} />}
            {step === 7 && <Step7 />}
            {step === 8 && <Step8 />}
            {step === 9 && <Step9 f={f} patch={patch} />}

            <div className="flex items-center justify-between pt-2">
              <button onClick={back} disabled={step===1} className="btn-ghost disabled:opacity-30" data-testid="wiz-back"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
              <button onClick={next} disabled={loading} className="btn-nexus" data-testid={step===9 ? "wiz-finish" : "wiz-next"}>
                {loading ? "…" : step === 9 ? "Agree & finish" : "Continue"}
                {step !== 9 && <ArrowRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProgressList({ step, studio }) {
  const steps = ["Basics","Studio + referral","Studio KYC","Hobbies & Interests","Your profile","Referral link","Invite friends","Buy NX bundle","Legal & finish"];
  return (
    <ol className="space-y-1.5 text-xs" data-testid="wiz-progress">
      {steps.map((label, i) => {
        const n = i + 1;
        const disabled = n === 3 && !studio;
        const active = n === step, done = n < step;
        return (
          <li key={n} className={`flex items-center gap-2 ${disabled ? "opacity-30" : ""}`}>
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full border ${active ? "border-nexus-cyan text-nexus-cyan" : done ? "border-nexus-mint text-nexus-mint" : "border-white/20 text-white/50"}`}>
              {done ? <Check className="w-3 h-3"/> : n}
            </span>
            <span className={active ? "text-white" : "text-white/50"}>{label}{disabled && " (skipped)"}</span>
          </li>
        );
      })}
    </ol>
  );
}

// ===== STEPS ===============================================================
function Step1({ f, patch, age }) {
  return (
    <div className="space-y-3" data-testid="wiz-step-1">
      <SectionHeader n={1} title="Tell us about you" />
      <input placeholder="Full legal name" value={f.full_name} onChange={(e)=>patch({full_name:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-fullname" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] tracking-widest uppercase text-white/40">Date of birth</label>
          <input type="date" value={f.dob} onChange={(e)=>patch({dob:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-dob" />
        </div>
        <div>
          <label className="text-[10px] tracking-widest uppercase text-white/40">Age</label>
          <input value={age} readOnly placeholder="—" className="w-full bg-white/5 border border-white/10 px-3 py-2.5 text-white/60" data-testid="wiz-age" />
        </div>
      </div>
      <div>
        <label className="text-[10px] tracking-widest uppercase text-white/40">Country · used for ad targeting & currency</label>
        <select value={f.country} onChange={(e)=>patch({country:e.target.value})} className="w-full bg-nexus-surface border border-white/10 px-3 py-2.5 text-sm" data-testid="wiz-country-main">
          <option value="">— select —</option>
          {COUNTRIES.map((c)=><option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <input placeholder="Address (street, city, postcode)" value={f.address} onChange={(e)=>patch({address:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-address" />
      <input placeholder="Email" type="email" autoCapitalize="none" autoCorrect="off" autoComplete="email" value={f.email} onChange={(e)=>patch({email:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-email" />
      <input placeholder="Create a password (8+ chars)" type="password" value={f.password} onChange={(e)=>patch({password:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-password" />
      <div>
        <label className="text-[10px] tracking-widest uppercase text-white/40">PayPal email · required for NX bundles, ad campaigns and withdrawals</label>
        <input placeholder="you@paypal.com" type="email" autoCapitalize="none" autoCorrect="off" value={f.paypal_email} onChange={(e)=>patch({paypal_email:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-paypal-main" />
        <p className="text-[10px] text-white/40 mt-1">You can change this any time from Settings → PayPal.</p>
      </div>
      <p className="text-[10px] text-white/40">You can change your password any time from /settings.</p>
    </div>
  );
}

function Step2({ f, patch }) {
  return (
    <div className="space-y-3" data-testid="wiz-step-2">
      <SectionHeader n={2} title="Studio Tier & referral" />
      <label className={`flex gap-3 items-start p-4 border cursor-pointer transition-colors ${f.studio_signup ? "border-nexus-cyan bg-nexus-panel" : "border-white/10 hover:border-white/30"}`} data-testid="wiz-studio-toggle">
        <input type="checkbox" checked={f.studio_signup} onChange={(e)=>patch({studio_signup: e.target.checked})} className="mt-0.5" />
        <div>
          <div className="font-display font-bold text-sm">Enroll me in Studio Tier · $20 AUD / month</div>
          <div className="text-[11px] text-white/50 mt-1">Extended stream quota, business suite, ED3N AI coach, faster payouts. Cancel any time.</div>
        </div>
      </label>
      <div>
        <label className="text-[10px] tracking-widest uppercase text-white/40">Referral code (optional)</label>
        <input placeholder="e.g. EDENREF1" value={f.ref} onChange={(e)=>patch({ref: e.target.value.toUpperCase()})} className="w-full bg-transparent border border-white/10 px-3 py-2.5 font-mono" data-testid="wiz-ref" />
        <p className="text-[10px] text-white/40 mt-1">Enter a friend&apos;s referral code. When you buy Studio Tier, you both receive <b className="text-nexus-mint">+500 NX</b>. Free bonus NX values are set by NEXUS closer to launch.</p>
      </div>
    </div>
  );
}

function Step3({ f, patch }) {
  return (
    <div className="space-y-3" data-testid="wiz-step-3">
      <SectionHeader n={3} title="Studio KYC & payment" />
      <p className="text-[11px] text-white/55">Required for creator payouts. Photo ID is private — only visible to the platform owner. You&apos;ll be able to purchase Studio Tier at the end via PayPal or Google Pay.</p>
      <label className="text-[10px] tracking-widest uppercase text-white/40">Tax country</label>
      <select value={f.tax_country} onChange={(e)=>patch({tax_country:e.target.value})} className="w-full bg-nexus-surface border border-white/10 px-3 py-2.5 text-sm" data-testid="wiz-country">
        <option value="">— select —</option>
        {COUNTRIES.map((c)=><option key={c} value={c}>{c}</option>)}
      </select>
      <input placeholder="PayPal email (where we&apos;ll pay you)" value={f.paypal_email} onChange={(e)=>patch({paypal_email:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-paypal" />
      <FileUpload label="Photo ID (driver&apos;s licence or passport)" accept="image/*" testid="wiz-photo-id" onUpload={(url)=>patch({photo_id_url:url})} value={f.photo_id_url} kind="image" />
      <p className="text-[10px] text-white/40">You&apos;ll pay for Studio Tier at the end (final step). All info encrypted at rest.</p>
    </div>
  );
}

function Step4({ f, patch }) {
  const [cats, setCats] = useState({});
  useEffect(() => { api.get("/interests").then(({data}) => setCats(data)).catch(() => {}); }, []);
  const toggle = (i) => patch({ interests: f.interests.includes(i) ? f.interests.filter(x=>x!==i) : [...f.interests, i] });
  return (
    <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="wiz-step-4">
      <SectionHeader n={4} title="Hobbies & interests" />
      <p className="text-[11px] text-white/50">Pick as many as you like. We use these to show you streams, reels, and ads you actually care about — and advertisers can target their campaigns to people who share these interests.</p>
      {Object.entries(cats).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-[10px] tracking-widest uppercase text-white/40 mt-2 mb-1">{cat}</div>
          <div className="flex flex-wrap gap-1.5">
            {items.map((i) => {
              const on = f.interests.includes(i);
              return (
                <button key={i} onClick={()=>toggle(i)}
                  data-testid={`wiz-interest-${i.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${on ? "bg-nexus-cyan text-black border-nexus-cyan" : "border-white/15 text-white/70 hover:border-white/30"}`}>
                  {i}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="text-[11px] text-white/50 pt-2">Selected: <b>{f.interests.length}</b></div>
    </div>
  );
}

function Step5({ f, patch }) {
  const setSocial = (k, v) => patch({ socials: { ...f.socials, [k]: v } });
  return (
    <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="wiz-step-5">
      <SectionHeader n={5} title="Set up your public profile" />
      <input placeholder="Display name (shown publicly)" value={f.display_name} onChange={(e)=>patch({display_name:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2.5" data-testid="wiz-displayname" />
      <FileUpload label="Profile banner · 1500×500 px recommended · JPG/PNG" accept="image/*" testid="wiz-banner" onUpload={(url)=>patch({banner_url:url})} value={f.banner_url} kind="image" />
      <FileUpload label="Avatar · square 400×400 px · JPG/PNG" accept="image/*" testid="wiz-avatar" onUpload={(url)=>patch({avatar_url:url})} value={f.avatar_url} kind="image" />
      <FileUpload label="Intro video · ≤15 seconds · MP4/MOV" accept="video/*" testid="wiz-intro" onUpload={(url)=>patch({intro_video_url:url})} value={f.intro_video_url} kind="video" maxSec={15} />
      <textarea placeholder="Bio (500 chars max)" value={f.bio} onChange={(e)=>patch({bio:e.target.value.slice(0,500)})} rows={3} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="wiz-bio" />
      <div className="text-[10px] text-right text-white/40">{f.bio.length}/500</div>

      <div className="text-[10px] tracking-widest uppercase text-white/40 pt-2">Social links (optional)</div>
      {SOCIAL_KEYS.map((s) => (
        <div key={s.id} className="flex items-center gap-2">
          <span className="w-24 text-xs text-white/60">{s.label}</span>
          <input value={f.socials[s.id] || ""} onChange={(e)=>setSocial(s.id, e.target.value)} placeholder={`${s.prefix}yourhandle`} className="flex-1 bg-transparent border border-white/10 px-3 py-1.5 text-sm" data-testid={`wiz-social-${s.id}`} />
        </div>
      ))}
      <input placeholder="Website (https://…)" value={f.website} onChange={(e)=>patch({website:e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="wiz-website" />

      <div className="text-[10px] tracking-widest uppercase text-white/40 pt-2">Privacy</div>
      <ToggleRow label="Show my birthday on my public profile" checked={f.show_dob} onChange={(v)=>patch({show_dob:v})} testid="wiz-show-dob" />
      <ToggleRow label="Show my marital status" checked={f.show_marital} onChange={(v)=>patch({show_marital:v})} testid="wiz-show-marital" />
      {f.show_marital && (
        <select value={f.marital_status} onChange={(e)=>patch({marital_status:e.target.value})} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm">
          <option value="">— status —</option><option>Single</option><option>In a relationship</option><option>Married</option><option>Complicated</option><option>Prefer not to say</option>
        </select>
      )}
      <ToggleRow label="Show my linked kids accounts (parental)" checked={f.show_kids} onChange={(v)=>patch({show_kids:v})} testid="wiz-show-kids" />
      <ToggleRow label="I&apos;m a parent — set me up with a Parental account" checked={f.is_parent} onChange={(v)=>patch({is_parent:v})} testid="wiz-is-parent" />
    </div>
  );
}

function Step6({ f }) {
  const code = f.ref ? "(you were referred by " + f.ref + ")" : "";
  const previewCode = "TEMP-" + (f.email || "you").split("@")[0].toUpperCase().slice(0, 8);
  const previewLink = `https://nexus-live.app/auth?mode=signup&ref=${previewCode}`;
  const share = (net) => {
    const text = `Join me on NEXUS Live — real streamers, tip in NX, live gaming reels. ${previewLink}`;
    const urls = {
      twitter:   `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      facebook:  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(previewLink)}`,
      whatsapp:  `https://wa.me/?text=${encodeURIComponent(text)}`,
      email:     `mailto:?subject=${encodeURIComponent("Join me on NEXUS")}&body=${encodeURIComponent(text)}`,
    };
    window.open(urls[net], "_blank", "noopener");
  };
  return (
    <div className="space-y-3" data-testid="wiz-step-6">
      <SectionHeader n={6} title="Your referral link" />
      <p className="text-[11px] text-white/55">{code}</p>
      <div className="glass-strong p-4 space-y-2">
        <div className="text-[10px] tracking-widest uppercase text-nexus-cyan">// your personal code (final after signup)</div>
        <div className="font-mono text-nexus-cyan text-lg" data-testid="wiz-ref-preview">{previewCode}</div>
        <div className="text-[11px] text-white/50 break-all">{previewLink}</div>
        <button onClick={() => { navigator.clipboard.writeText(previewLink); toast.success("Link copied"); }} className="btn-ghost text-[10px]"><Copy className="w-3 h-3" /> Copy link</button>
      </div>
      <div className="text-[10px] tracking-widest uppercase text-white/40 pt-2">Share on</div>
      <div className="flex gap-2">
        <button onClick={()=>share("twitter")}  className="btn-ghost text-[10px]" data-testid="share-twitter"><Share2 className="w-3 h-3" />X</button>
        <button onClick={()=>share("facebook")} className="btn-ghost text-[10px]" data-testid="share-facebook"><Share2 className="w-3 h-3" />Facebook</button>
        <button onClick={()=>share("whatsapp")} className="btn-ghost text-[10px]" data-testid="share-whatsapp"><Share2 className="w-3 h-3" />WhatsApp</button>
        <button onClick={()=>share("email")}    className="btn-ghost text-[10px]" data-testid="share-email"><Mail className="w-3 h-3" />Email</button>
      </div>
      <p className="text-[10px] text-white/40 pt-1">You&apos;ll receive your permanent code as soon as you finish signup. Both parties get <b className="text-nexus-mint">+500 NX</b> when the referee buys Studio Tier.</p>
    </div>
  );
}

function Step7() {
  const [emails, setEmails] = useState("");
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickerReady] = useState(() => typeof window !== "undefined" && "contacts" in navigator && "ContactsManager" in window);
  const pickContacts = async () => {
    try {
      const contacts = await navigator.contacts.select(["email"], { multiple: true });
      const list = contacts.flatMap(c => c.email || []);
      if (list.length) setEmails((prev) => (prev ? prev + ", " : "") + list.join(", "));
    } catch (e) { toast.error("Contacts picker cancelled"); }
  };
  const scan = async () => {
    setBusy(true);
    try {
      const list = emails.split(/[\s,]+/).filter(Boolean).slice(0, 500);
      // Note: this endpoint requires auth. For SIGNUP flow, we surface it as a demo
      // — a fresh signup hasn't got a token yet. We call it best-effort and swallow 401s.
      const { data } = await api.post("/friends/find_by_email", { emails: list });
      setFound(data.items || []);
    } catch { setFound([]); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="wiz-step-7">
      <SectionHeader n={7} title="Invite friends already on Nexus" />
      <p className="text-[11px] text-white/55">Paste emails (comma-separated) — we&apos;ll show which of your friends already have Nexus accounts, and you can send everyone else an invite.</p>
      {pickerReady && (
        <button onClick={pickContacts} className="btn-ghost text-[10px]" data-testid="pick-contacts"><Users className="w-3 h-3" /> Pick from phone contacts</button>
      )}
      <textarea rows={3} value={emails} onChange={(e)=>setEmails(e.target.value)} placeholder="friend1@example.com, friend2@example.com…" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="wiz-friends-emails" />
      <div className="flex gap-2">
        <button onClick={scan} disabled={busy} className="btn-nexus text-[10px]" data-testid="scan-friends">{busy ? "Scanning…" : "Find friends on Nexus"}</button>
      </div>
      {found.length > 0 && (
        <div className="glass-strong p-3 space-y-1" data-testid="found-friends">
          <div className="text-[10px] tracking-widest uppercase text-nexus-mint">// already on nexus</div>
          {found.map((u) => (
            <div key={u.id} className="flex items-center gap-2 text-xs">
              <img src={u.avatar} alt="" className="w-6 h-6 rounded-full" />
              <span className="font-medium">{u.display_name}</span>
              <span className="text-white/40">· {u.role}</span>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-white/40">Email invite links go out from your own address book once you finish signup. Contacts stay on your device.</p>
    </div>
  );
}

function Step8() {
  const [bundles, setBundles] = useState([]);
  const [pick, setPick] = useState(null);
  useEffect(() => { api.get("/coins/bundles").then(({data})=>setBundles(data)).catch(()=>{}); }, []);
  return (
    <div className="space-y-3" data-testid="wiz-step-8">
      <SectionHeader n={8} title="Buy Nexus Coins (optional)" />
      <p className="text-[11px] text-white/55">Send stickers, tips, and gifts to your favourite streamers. Skip if you want — you can always top up later.</p>
      <div className="grid grid-cols-2 gap-2">
        {bundles.map((b) => {
          const active = pick === b.id;
          return (
            <button key={b.id} onClick={()=>setPick(b.id)}
              data-testid={`bundle-${b.id}`}
              className={`text-left p-3 border transition-colors ${active ? "border-nexus-cyan bg-nexus-panel" : "border-white/10 hover:border-white/30"}`}>
              <div className="font-display font-bold">{b.coins.toLocaleString()} NX</div>
              <div className="text-[11px] text-white/50">${b.price.toFixed(2)} {b.currency || "AUD"} · {b.label}</div>
            </button>
          );
        })}
      </div>
      {pick && (
        <div className="glass-strong p-3 space-y-2">
          <div className="text-[10px] tracking-widest uppercase text-nexus-cyan">// pay now</div>
          <PayPalCheckout
            purpose="nx_bundle"
            amountUSD={Number(bundles.find(b=>b.id===pick)?.price || 0)}
            refId={pick}
            label="Buy NX bundle"
            onSuccess={() => toast.success("NX credited — you can send stickers now")}
          />
        </div>
      )}
    </div>
  );
}

function Step9({ f, patch }) {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCommunity, setShowCommunity] = useState(false);
  return (
    <div className="space-y-3" data-testid="wiz-step-9">
      <SectionHeader n={9} title="Rules, privacy, and finish" />
      <p className="text-[11px] text-white/55">Read each document and tick the boxes. These are the rules of NEXUS — we enforce them.</p>

      <LegalRow open={showTerms} onToggle={()=>setShowTerms(v=>!v)} docId="terms" label="Terms of Service" checked={f.agreed_to_tos} onCheck={(v)=>patch({agreed_to_tos:v})} testid="agree-tos" />
      <LegalRow open={showPrivacy} onToggle={()=>setShowPrivacy(v=>!v)} docId="privacy" label="Privacy Policy" checked={f.agreed_to_kyc} onCheck={(v)=>patch({agreed_to_kyc:v})} testid="agree-privacy" />
      <LegalRow open={showCommunity} onToggle={()=>setShowCommunity(v=>!v)} docId="community" label="Community Guidelines · no discrimination / nudity / abuse / racism" checked={f.agreed_to_community} onCheck={(v)=>patch({agreed_to_community:v})} testid="agree-community" />

      {f.studio_signup && (
        <label className="flex gap-2 text-xs text-white/80 cursor-pointer p-3 border border-white/10">
          <input type="checkbox" className="mt-0.5" checked={f.agreed_to_billing} onChange={(e)=>patch({agreed_to_billing:e.target.checked})} data-testid="agree-billing" />
          <span>I agree to be billed <b>$20 AUD / month</b> for Studio Tier via PayPal. Cancel any time.</span>
        </label>
      )}

      <p className="text-[10px] text-white/40">After you finish, you&apos;ll land on your own profile — set up, ready to stream, tip, and post.</p>
    </div>
  );
}

// ===== Reusable UI =========================================================
function SectionHeader({ n, title }) {
  return (
    <div>
      <div className="label-eyebrow mb-1">// step {n} · {title.toLowerCase()}</div>
      <h3 className="font-display font-black text-2xl">{title}</h3>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={(e)=>onChange(e.target.checked)} />
      <span dangerouslySetInnerHTML={{__html: label}} />
    </label>
  );
}

function LegalRow({ open, onToggle, docId, label, checked, onCheck, testid }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => { if (open && !doc) api.get(`/legal/${docId}`).then(({data})=>setDoc(data)).catch(()=>{}); }, [open, docId, doc]);
  return (
    <div className="border border-white/10 p-3 space-y-2">
      <label className="flex items-center gap-2 text-xs text-white/80 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={(e)=>onCheck(e.target.checked)} data-testid={testid} />
        <span>I agree to the <button type="button" onClick={onToggle} className="text-nexus-cyan underline">{label}</button></span>
      </label>
      {open && (
        <div className="max-h-52 overflow-y-auto bg-black/40 border border-white/10 p-3 text-[11px] text-white/70 whitespace-pre-wrap font-mono">
          {doc ? doc.body : "Loading…"}
        </div>
      )}
    </div>
  );
}

function FileUpload({ label, accept, onUpload, value, kind = "image", maxSec, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (kind === "video" && maxSec) {
      // validate duration client-side
      const dur = await new Promise((res) => {
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => res(v.duration);
        v.onerror = () => res(999);
        v.src = URL.createObjectURL(file);
      });
      if (dur > maxSec) { toast.error(`Video must be ${maxSec}s or shorter`); return; }
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const endpoint = kind === "video" ? "/upload/video" : "/upload/image";
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const abs = data.url.startsWith("http") ? data.url : `${backend}${data.url}`;
      onUpload(abs);
      toast.success("Uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div>
      <label className="text-[10px] tracking-widest uppercase text-white/40">{label}</label>
      <div className="flex items-center gap-2">
        <button type="button" onClick={()=>ref.current?.click()} disabled={busy} className="btn-ghost text-[10px]" data-testid={testid}>
          {kind === "video" ? <Video className="w-3 h-3" /> : <Camera className="w-3 h-3" />}
          {busy ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
        <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e)=>{const f=e.target.files?.[0]; if(f) upload(f);}} />
        {value && <span className="text-[10px] text-nexus-mint truncate max-w-[60%]">✓ {value.split("/").pop()}</span>}
      </div>
    </div>
  );
}
